// Slice RTK per gli stream con listener Firebase (`{ list, unsubscribe }`).
//
// Pattern condiviso: ogni slice gestisce una collection Firestore via
// `onSnapshot`. Lo stato è `{ list: [], unsubscribe: null }`. Le azioni:
//
//   - `set(list)` / `received(list)`: rimpiazza la lista (alias semantici).
//   - `merge(list)`: merge by `id` con lo state corrente, opzionale filter
//     `!deleted`, opzionale trim a `maxItems` elementi più recenti.
//   - `listenerStarted(unsubscribeFn)`: salva la funzione di cleanup.
//   - `listenerStopped()`: clear list + clear unsubscribe (chiamato dopo
//     `unsubscribe()` lato thunk).
//   - `listenerUnsubscribeOnly()`: clear unsubscribe MA mantiene la list
//     (utile quando si rifa la query senza dover svuotare la UI).
//
// I thunk in `src/store/actions/<Xxx>Actions.js` dispatchano queste action
// creator (e non più raw `{ type, payload }`). I consumer leggono
// `state.<key>.list` e `state.<key>.unsubscribe` come prima — shape
// preservato per back-compat.

import { createSlice } from '@reduxjs/toolkit';

/**
 * @param {string} name             Nome della chiave di stato (es. 'orders').
 * @param {object} [opts]
 * @param {number} [opts.maxItems]  Trim della lista nel `merge` (slice -N).
 * @param {boolean} [opts.filterDeleted=false] Se `true`, dropa gli item con
 *   `deleted === true` nel `merge`.
 * @param {boolean} [opts.withRangeKey=false] Se `true`, aggiunge `rangeKey`
 *   allo stato + reducer `rangeSet` (usato da `orders` per tracciare il range
 *   data attivo ed evitare re-listen ridondanti).
 */
function makeStreamSlice(name, opts = {}) {
  const { maxItems, filterDeleted = false, withRangeKey = false } = opts;
  const initialState = withRangeKey
    ? { list: [], unsubscribe: null, rangeKey: null }
    : { list: [], unsubscribe: null };

  const slice = createSlice({
    name,
    initialState,
    reducers: {
      set: (state, action) => {
        state.list = action.payload ?? [];
      },
      rangeSet: (state, action) => {
        state.rangeKey = action.payload ?? null;
      },
      received: (state, action) => {
        state.list = action.payload ?? [];
      },
      merge: (state, action) => {
        const incoming = action.payload ?? [];
        const map = new Map();
        state.list.forEach((e) => map.set(e.id, e));
        incoming.forEach((e) => map.set(e.id, e));
        let merged = Array.from(map.values());
        if (filterDeleted) merged = merged.filter((e) => !e.deleted);
        if (maxItems && merged.length > maxItems) merged = merged.slice(-maxItems);
        state.list = merged;
      },
      listenerStarted: (state, action) => {
        state.unsubscribe = action.payload ?? null;
      },
      listenerStopped: (state) => {
        state.list = [];
        state.unsubscribe = null;
      },
      listenerUnsubscribeOnly: (state) => {
        state.unsubscribe = null;
      },
    },
  });

  return {
    reducer: slice.reducer,
    actions: slice.actions,
  };
}

const orders = makeStreamSlice('orders', {
  maxItems: 5000,
  filterDeleted: false,
  withRangeKey: true,
});
const dailyTrips = makeStreamSlice('daily_trips');
const events = makeStreamSlice('events', { maxItems: 2000, filterDeleted: true });
const driverShifts = makeStreamSlice('driverShifts', { maxItems: 2000, filterDeleted: true });
const driverVehicleAssignments = makeStreamSlice('driverVehicleAssignments', {
  maxItems: 2000,
  filterDeleted: true,
});
const users = makeStreamSlice('users');

// --- Reducers ---------------------------------------------------------------
export const streamReducers = {
  orders: orders.reducer,
  daily_trips: dailyTrips.reducer,
  events: events.reducer,
  driverShifts: driverShifts.reducer,
  driverVehicleAssignments: driverVehicleAssignments.reducer,
  users: users.reducer,
};

// --- Action creators per i thunk --------------------------------------------
export const ordersActions = orders.actions;
export const dailyTripsActions = dailyTrips.actions;
export const eventsActions = events.actions;
export const driverShiftsActions = driverShifts.actions;
export const driverVehicleAssignmentsActions = driverVehicleAssignments.actions;
export const usersActions = users.actions;
